import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { DemoConfiguration } from "data/models";
import { fetchDemos } from "data/queries-common";
import addressAutocompleteDark from "icons/address-autocomplete-dark.png";
import addressAutocompleteLight from "icons/address-autocomplete-light.png";
import chatswood from "icons/chatswood.png";
import easementsDark from "icons/easements-dark.png";
import easementsLight from "icons/easements-light.png";
import emissionsMetric from "icons/emissionsMetric.jpg";
import floodDark from "icons/flood-dark.png";
import floodLight from "icons/flood-light.png";
import localityAutocompleteDark from "icons/locality-autocomplete-dark.png";
import localityAutocompleteLight from "icons/locality-autocomplete-light.png";
import nabDark from "icons/nab-dark.png";
import nabLight from "icons/nab-light.png";
import propertyInsightsDark from "icons/property-insights-dark.png";
import propertyInsightsLight from "icons/property-insights-light.png";
import electricityMarketIcon from "icons/Stylized-Lightning-Bolt.png";
import { useQuery } from "@tanstack/react-query";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { ChatswoodBuildings } from "../demos/chatswood-buildings/chatswood-buildings";
import { Easements } from "../demos/easements/easements";
import { ElectricityMarket } from "../demos/electricity-market/electricity-market";
import { EmissionMetricPage } from "../demos/emission-metric/emision-metric-page";
import { FloodDataPage } from "../demos/flood-data/flood-data-page";
import { NabLandUsePage } from "../demos/nab-land-use/nab-land-use-page";
import { NationalBuildingsChatswood } from "../demos/national-buildings-chatswood/national-buildings-chatswood";
import { predictiveAddress } from "../demos/predictive-address/predictive-address-demo";
import { predictiveSuburb } from "../demos/predictive-suburb/predictive-suburb-demo";
import { PropertyInsights } from "../demos/property-insights/property-insights";
import { DemoCard } from "./demo-card";
import { OctagonAlert } from "lucide-react";

export interface DemoItem {
  id: string;
  title: string;
  categories: string[];
  description: string;
  path: string;
  darkThumbnail: string;
  lightThumbnail: string;
  component: React.ComponentType<any>;
}

// Id should match the id in backend
export const DataDemos: DemoItem[] = [
  {
    id: "emissions-metric",
    title: "Emissions Metric",
    categories: ["Climate"],
    description:
      "Visualise emissions metrics for property portfolios, supporting environmental compliance and informed decision making.",
    path: Routes.emissionsMetric,
    darkThumbnail: emissionsMetric,
    lightThumbnail: emissionsMetric,
    component: EmissionMetricPage,
  },
  {
    id: "predictive-address",
    title: "Address Autocomplete",
    categories: ["Risk", "UX", "Service Delivery", "Ecommerce"],
    description:
      "Predictive address auto-completion with real-time map visualisation. Enhance user experience and operational efficiency.",
    path: Routes.predictiveAddress,
    darkThumbnail: addressAutocompleteDark,
    lightThumbnail: addressAutocompleteLight,
    component: predictiveAddress,
  },
  {
    id: "predictive-locality",
    title: "Locality Autocomplete",
    categories: ["Risk", "UX", "Service Delivery", "Ecommerce"],
    description:
      "Predictive locality auto-completion with real-time map visualisation. Enhance user experience and operational efficiency.",
    path: Routes.predictiveLocality,
    darkThumbnail: localityAutocompleteDark,
    lightThumbnail: localityAutocompleteLight,
    component: predictiveSuburb,
  },
  {
    id: "easements",
    title: "Easements",
    categories: ["Utilities", "Access", "Rights", "Development"],
    description:
      "Explore our easement data demo — gain property insights to streamline planning and identify restrictions",
    path: Routes.easements,
    darkThumbnail: easementsDark,
    lightThumbnail: easementsLight,
    component: Easements,
  },
  {
    id: "flood-data-demo",
    title: "Flood Data Demo",
    categories: [],
    description:
      "See flood risk through new eyes. Our interactive demonstration reveals how the Ingham community was affected by flooding, using sophisticated modelling and class-leading spatial insights from Geoscape that go beyond traditional risk assessments.",
    path: Routes.floodDataDemo,
    darkThumbnail: floodDark,
    lightThumbnail: floodLight,
    component: FloodDataPage,
  },
  {
    id: "nab-land-use",
    title: "NAB land use",
    categories: [],
    description: "",
    path: Routes.nabLandUse,
    darkThumbnail: nabDark,
    lightThumbnail: nabLight,
    component: NabLandUsePage,
  },
  {
    id: "property-insights",
    title: "Property Insights",
    categories: [],
    description: "",
    path: Routes.propertyInsights,
    darkThumbnail: propertyInsightsDark,
    lightThumbnail: propertyInsightsLight,
    component: PropertyInsights,
  },
  {
    id: "chatswood-buildings",
    title: "Chatswood Demo",
    categories: [
      "Built Environment",
      "Risk",
      "Planning",
      "Surface Cover",
      "Digital Twin",
    ],
    description:
      "This demo showcases the Chatswood area as an interactive 3D digital twin. It brings together a wide range of national datasets, visualised in one place, allowing users to explore and experience Chatswood in a rich, data-driven environment.",
    path: Routes.chatswoodBuildings,
    darkThumbnail: chatswood,
    lightThumbnail: chatswood,
    component: ChatswoodBuildings,
  },
  {
    id: "national-buildings-chatswood",
    title: "National Buildings (Chatswood)",
    categories: [
      "Built Environment",
      "Buildings",
      "National Data",
      "Spatial Analysis",
    ],
    description:
      "New National Buildings Product. Explore the interactive demo of Chatswood to see the data in action and experience the product's capabilities. Once you've explored, you can also download a sample dataset to take a closer look at the new Insight Pack.",
    path: Routes.nationalBuildingsChatswood,
    darkThumbnail: chatswood,
    lightThumbnail: chatswood,
    component: NationalBuildingsChatswood,
  },
  {
    id: "electricity-market",
    title: "Electricity Market",
    categories: ["Energy", "Infrastructure", "Market Data"],
    description:
      "Proof of concept demo for exploring the electricity market data with visualisations. Missing WA and NT electricity data. Emissions data has been estimated, but it is possible to obtain this data too.",
    path: Routes.electricityMarket,
    darkThumbnail: electricityMarketIcon,
    lightThumbnail: electricityMarketIcon,
    component: ElectricityMarket,
  },
];

export const DemosPage = () => {
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const demosQuery = useQuery({
    queryKey: [QueryKeys.demosKey, identityId],
    queryFn: () => fetchDemos(),
  });

  const getDemoItem = (demoId: string): DemoItem | undefined => {
    return DataDemos.find((item) => item.id === demoId);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {demosQuery.isPending &&
        DataDemos.map((demoItem: DemoItem, index: number) => (
          <Skeleton className="h-64" key={index} />
        ))}
      {demosQuery.isSuccess &&
        demosQuery.data.map((demoItem: DemoConfiguration, index: number) => (
          <div key={index}>
            {getDemoItem(demoItem.id) !== undefined && (
              <DemoCard
                {...getDemoItem(demoItem.id)!}
                status={demoItem.status}
              ></DemoCard>
            )}
          </div>
        ))}
      {demosQuery.isError && (
        <Alert variant="destructive">
          <OctagonAlert className="size-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            An error occurred while fetching the demos.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
